// @ts-nocheck
/* eslint-disable */
// 兼容 shim：确保 window.icons / window.UtilsModule / window.i18nModule 等已就绪
// 后续会逐步迁移到显式 import
import * as React from 'react';
import {
  Alert,
  ActionButton,
  Card,
  Descriptions,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from '@ui';
import '@lib/api';
import '@lib/auth';
import { getCurrentUser } from '@lib/auth';
import '@lib/brand';
import '@lib/utils';
import '@lib/i18n';
import '@ui';
const { icons } = window as any;
const {
  PlusOutlined,
  ReloadOutlined,
  MobileOutlined,
  ProfileOutlined,
  LaptopOutlined,
  BookOutlined,
  EditOutlined,
  CloseOutlined,
} = icons;

/**
 * DeviceManagement - 设备管理组件
 *
 * 功能：
 * - 设备列表展示（含终端版本、连接状态、标签）
 * - 设备详情查看（分组卡片展示）
 * - 设备信息编辑（别名 + 标签）
 * - 设备驱逐删除
 * - 客户端认证密钥管理（检查未绑定密钥、安装命令）
 * - 连接历史记录
 *
 * 依赖：
 * - APIModule (api.js)
 * - UtilsModule (utils.js)
 * - I18nModule (i18n.js)
 * - @ui 组件
 */
('use strict');

// 引入依赖
const { API_BASE, apiRequest } = window;
const { useState, useEffect, useRef, useCallback } = React;

const { Title, Text, Paragraph } = Typography;
// 多语言翻译函数
const t = (key, params) => (window.i18nModule ? window.i18nModule.t(key, params) : key);

// 标签颜色列表（与 Tag 组件支持的颜色对齐）
const TAG_COLORS = [
  'blue', 'cyan', 'green', 'purple',
  'red', 'orange', 'yellow',
];

// 根据标签名生成稳定颜色
function getTagColor(tagName) {
  let hash = 0;
  for (let i = 0; i < tagName.length; i++) {
    hash = tagName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

// TagInput 组件：标签编辑 — 已有标签下拉选择 + 输入创建
function TagInput({ value = [], onChange, userTags = [] }) {
  const [inputVal, setInputVal] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const addTag = (tag) => {
    const normalized = tag.trim().toLowerCase();
    if (!normalized) return;
    if (!/^[a-z0-9\-_]{1,30}$/.test(normalized)) {
      message.warning(t('deviceManagement.tagRule'));
      return;
    }
    if (value.includes(normalized)) return;
    if (value.length >= 10) return;
    onChange([...value, normalized]);
    setInputVal('');
    inputRef.current?.focus();
  };

  const removeTag = (tag) => {
    onChange(value.filter((v) => v !== tag));
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && inputVal) {
      e.preventDefault();
      addTag(inputVal);
    } else if (e.key === 'Backspace' && !inputVal && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  // 过滤出匹配的下拉选项
  const filtered = userTags
    .filter((t) => !value.includes(t))
    .filter((t) => !inputVal || t.includes(inputVal.toLowerCase()));

  return (
    <div className="relative">
      {/* 已选标签 */}
      <div
        className="flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-md border border-dark-700 bg-dark-900 min-h-[38px] cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag) => (
          <Tag
            key={tag}
            color={getTagColor(tag)}
            closable
            onClose={() => removeTag(tag)}
            style={{ margin: 0, fontSize: 12 }}
          >
            {tag}
          </Tag>
        ))}
        <input
          ref={inputRef}
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={value.length === 0 ? t('deviceManagement.tagPlaceholder') : ''}
          className="flex-1 min-w-[80px] bg-transparent border-none outline-none text-sm text-white placeholder-dark-500"
          style={{ height: 24 }}
        />
      </div>
      {/* 下拉建议 */}
      {focused && filtered.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-dark-700 bg-dark-900 shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((tag) => (
            <div
              key={tag}
              className="px-3 py-2 text-sm text-dark-200 cursor-pointer hover:bg-dark-700"
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(tag);
              }}
            >
              <Tag color={getTagColor(tag)} style={{ margin: 0, fontSize: 11 }}>{tag}</Tag>
            </div>
          ))}
        </div>
      )}
      {/* 输入内容确认提示 */}
      {inputVal && /^[a-z0-9\-_]{1,30}$/.test(inputVal) && !value.includes(inputVal) && (
        <div className="mt-1 text-xs text-primary-400">
          {t('deviceManagement.tagAddTag')} &quot;{inputVal}&quot; (Enter / Space)
        </div>
      )}
    </div>
  );
}

// TagSearchInput 搜索标签组件：自定义 Input + 建议下拉
function TagSearchInput({ searchTags = [], setSearchTags, userTags = [] }) {
  const [inputVal, setInputVal] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const addTag = (tag) => {
    if (!tag) return;
    if (searchTags.includes(tag)) return;
    setSearchTags([...searchTags, tag]);
    setInputVal('');
    inputRef.current?.focus();
  };

  const removeTag = (tag) => {
    setSearchTags(searchTags.filter((v) => v !== tag));
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && inputVal) {
      e.preventDefault();
      addTag(inputVal.trim());
    } else if (e.key === 'Backspace' && !inputVal && searchTags.length > 0) {
      removeTag(searchTags[searchTags.length - 1]);
    }
  };

  const filtered = userTags
    .filter((v) => !searchTags.includes(v))
    .filter((v) => !inputVal || v.includes(inputVal.toLowerCase()));

  return (
    <div className="relative" style={{ minWidth: 220 }}>
      {/* 已选标签 + 输入框 */}
      <div
        className="flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-md border border-dark-700 bg-dark-900 min-h-[32px] cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {searchTags.map((tag) => (
          <Tag
            key={tag}
            color={getTagColor(tag)}
            closable
            onClose={() => removeTag(tag)}
            style={{ margin: 0, fontSize: 11 }}
          >
            {tag}
          </Tag>
        ))}
        <input
          ref={inputRef}
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={searchTags.length === 0 ? t('deviceManagement.tagSearch') : ''}
          className="flex-1 min-w-[60px] bg-transparent border-none outline-none text-sm text-white placeholder-dark-500"
          style={{ height: 22 }}
        />
      </div>
      {/* 下拉建议 */}
      {focused && filtered.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-dark-700 bg-dark-900 shadow-lg max-h-36 overflow-y-auto">
          {filtered.map((tag) => (
            <div
              key={tag}
              className="px-3 py-1.5 text-sm text-dark-200 cursor-pointer hover:bg-dark-700"
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(tag);
              }}
            >
              <Tag color={getTagColor(tag)} style={{ margin: 0, fontSize: 11 }}>{tag}</Tag>
            </div>
          ))}
        </div>
      )}
      {inputVal && !searchTags.includes(inputVal) && (
        <div className="mt-0.5 text-[11px] text-primary-400">
          {t('deviceManagement.tagAddTag')} &quot;{inputVal}&quot; (Enter / Space)
        </div>
      )}
    </div>
  );
}

// 设备管理组件
function DeviceManagement() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [connectionHistory, setConnectionHistory] = useState([]);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [updateForm] = Form.useForm();
  // 新版：选择安装令牌生成安装命令
  const [addDeviceModalVisible, setAddDeviceModalVisible] = useState(false);
  const [installTokens, setInstallTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState<string | undefined>(undefined);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [currentUsername, setCurrentUsername] = useState('');
  // 搜索用的标签
  const [searchTags, setSearchTags] = useState([]);
  // 用户的所有标签（用于编辑下拉建议）
  const [userTags, setUserTags] = useState([]);

  useEffect(() => {
    loadDevices();
    loadUserTags();
    // 每30秒自动刷新设备列表
    const interval = setInterval(loadDevices, 30000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // 搜索标签变化时重新加载
  useEffect(() => {
    loadDevices();
  }, [searchTags]);

  const loadUserTags = async () => {
    try {
      const { response, data } = await apiRequest(`${API_BASE}/tags`);
      if (response.ok) {
        setUserTags(data.tags || []);
      }
    } catch (e) {
      // ignore
    }
  };

  const loadDevices = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/devices`;
      if (searchTags.length > 0) {
        url += `?tags=${encodeURIComponent(searchTags.join(','))}`;
      }
      const { response, data, unauthorized } = await apiRequest(url);
      if (unauthorized) return;

      if (response.ok) {
        setDevices(data.devices || []);
      } else {
        message.error(data.error || t('deviceManagement.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (device) => {
    setLoading(true);
    try {
      const { response, data, unauthorized } = await apiRequest(`${API_BASE}/devices/${device.id}`);
      if (unauthorized) return;

      if (response.ok) {
        setSelectedDevice(data);
        setDetailModalVisible(true);
      } else {
        message.error(data.error || t('deviceManagement.loadFailed'));
      }
    } catch (error) {
      console.error(t('deviceManagement.loadFailed'), error);
      message.error(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = async (device) => {
    setSelectedDevice(device);
    setLoading(true);
    try {
      const { response, data, unauthorized } = await apiRequest(
        `${API_BASE}/devices/${device.id}/history`,
      );
      if (unauthorized) return;

      if (response.ok) {
        setConnectionHistory(data.history || []);
        setHistoryModalVisible(true);
      } else {
        message.error(data.error || t('deviceManagement.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (device) => {
    setSelectedDevice(device);
    updateForm.setFieldsValue({
      device_alias: device.device_alias,
      tags: device.tags || [],
    });
    setUpdateModalVisible(true);
  };

  const handleUpdateSubmit = async (values) => {
    try {
      const { response, data, unauthorized } = await apiRequest(
        `${API_BASE}/devices/${selectedDevice.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        },
      );
      if (unauthorized) return;

      if (response.ok) {
        message.success(t('deviceManagement.updateSuccess'));
        setUpdateModalVisible(false);
        updateForm.resetFields();
        loadDevices();
        loadUserTags();
      } else {
        message.error(data.error || t('deviceManagement.updateFailed'));
      }
    } catch (error) {
      message.error(t('common.networkError'));
    }
  };

  const handleDelete = async (device) => {
    Modal.confirm({
      title: t('deviceManagement.evict'),
      content: t('deviceManagement.evictConfirm', {
        name: device.device_alias || device.serial_number,
      }),
      onOk: async () => {
        try {
          const { response, data, unauthorized } = await apiRequest(
            `${API_BASE}/devices/${device.id}`,
            {
              method: 'DELETE',
            },
          );
          if (unauthorized) return;

          if (response.ok) {
            message.success(data.message || t('deviceManagement.evictSuccess'));
            loadDevices();
          } else {
            message.error(data.error || t('deviceManagement.evictFailed'));
          }
        } catch (error) {
          message.error(t('common.networkError'));
        }
      },
    });
  };

  // 打开"选择安装令牌生成安装命令"弹窗
  const openAddDeviceModal = async () => {
    setAddDeviceModalVisible(true);
    setSelectedToken(undefined);
    // 拉取当前用户名（用于命令中的 --username 参数）
    try {
      const u = getCurrentUser?.();
      if (u?.username) setCurrentUsername(u.username);
    } catch {
      /* 忽略，回退展示空 */
    }
    // 拉取该用户可用的安装令牌
    setLoadingTokens(true);
    try {
      const { response, data } = await apiRequest<{ tokens?: { id: number; token: string; note: string; enabled: boolean }[] }>(
        `${API_BASE}/install-tokens`,
      );
      if (response.ok) {
        const enabled = (data.tokens || []).filter((t) => t.enabled);
        setInstallTokens(enabled);
      }
    } catch {
      /* 留空即可，UI 上提示 */
    } finally {
      setLoadingTokens(false);
    }
  };

  // 关闭弹窗
  const closeAddDeviceModal = () => {
    setAddDeviceModalVisible(false);
    setSelectedToken(undefined);
    setInstallTokens([]);
  };

  // EditableCell 组件
  const EditableCell =
    window.EditableCell ||
    function ({ value, tags, onEdit }) {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <div
          className="flex items-center cursor-pointer"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={onEdit}
        >
          <div>
            <Text>{value}</Text>
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {tags.map((tag) => (
                  <Tag key={tag} color={getTagColor(tag)} style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>
                    {tag}
                  </Tag>
                ))}
              </div>
            )}
          </div>
          {isHovered && <ActionButton action="edit" iconOnly />}
        </div>
      );
    };

  const columns = [
    {
      title: t('deviceManagement.deviceAlias'),
      key: 'name',
      width: 240,
      render: (_, record) => (
        <EditableCell
          value={record.device_alias || record.serial_number}
          tags={record.tags}
          onEdit={() => handleUpdate(record)}
        />
      ),
    },
    {
      title: t('deviceManagement.hostname'),
      dataIndex: 'hostname',
      key: 'hostname',
      render: (text) => text || '-',
    },
    {
      title: t('deviceManagement.system'),
      key: 'os',
      render: (_, record) =>
        record.os_type ? `${record.os_type} ${record.os_version || ''}` : '-',
    },
    {
      title: t('deviceManagement.terminalVersion'),
      dataIndex: 'app_version',
      key: 'app_version',
      // 表头 "Terminal Version" 文字宽 111px + px-4 内边距 32px = 需 143px。
      // 原 100 会导致 nowrap 文字溢出并压到 Status 列上；取 150 留余量。
      width: 150,
      render: (text) => text || '-',
    },
    {
      title: t('deviceManagement.connectionStatus'),
      dataIndex: 'is_online',
      key: 'is_online',
      width: 130,
      render: (isOnline, record) => {
        const ip = record.client_ip;
        const displayIP =
          ip && ip.includes(':') && ip.length > 16
            ? ip.split(':').slice(0, 3).join(':') + '...'
            : ip;
        const unhealthy = isOnline && record.health_status === 'unhealthy';
        const reason = record.health_reason;
        return (
          <div>
            <span className="device-name-with-status">
              <span
                className={`device-status-dot ${unhealthy ? 'offline' : isOnline ? 'online' : 'offline'}`}
                style={unhealthy ? { background: '#e6a817' } : undefined}
              />
              {unhealthy ? (
                <Tooltip title={t(`deviceManagement.healthReasons.${reason}`)}>
                  <span
                    className="text-yellow-400"
                    style={{ fontSize: 13, borderBottom: '1px dashed #e6a817' }}
                  >
                    {t('deviceManagement.unhealthy')}
                  </span>
                </Tooltip>
              ) : (
                <span
                  className={isOnline ? 'text-green-400' : 'text-dark-600'}
                  style={{ fontSize: 13 }}
                >
                  {isOnline ? t('deviceManagement.online') : t('deviceManagement.offline')}
                </span>
              )}
            </span>
            {isOnline && !unhealthy && ip && (
              <Tooltip title={ip}>
                <div className="text-dark-500 ml-3.5 mt-0.5" style={{ fontSize: 11 }}>
                  {displayIP}
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: t('common.operation'),
      key: 'action',
      // 100 装不下「操作 ▼」按钮的自然宽度：中文 CJK 可逐字断行，
      // 按钮被压缩后文字会竖排成「操/作/▼」（实测占 2~3 行）。
      // 加宽到 120 留出余量；nowrap+truncate 是列再被压窄时的兜底。
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'detail',
                label: t('deviceManagement.detail'),
                onClick: () => handleViewDetail(record),
              },
              {
                key: 'history',
                label: t('deviceManagement.history'),
                onClick: () => handleViewHistory(record),
              },
              {
                key: 'edit',
                label: t('deviceManagement.edit'),
                onClick: () => handleUpdate(record),
              },
              {
                type: 'divider',
              },
              {
                key: 'delete',
                label: t('deviceManagement.evict'),
                danger: true,
                onClick: () => handleDelete(record),
              },
            ],
          }}
          trigger={['click']}
          placement="bottomRight"
          getPopupContainer={() => document.body}
        >
          <ActionButton action="more">{t('common.operation')} ▼</ActionButton>
        </Dropdown>
      ),
    },
  ];

  const historyColumns = [
    {
      title: t('deviceManagement.connectTime'),
      dataIndex: 'connected_at',
      key: 'connected_at',
      render: (text) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: t('deviceManagement.disconnectTime'),
      dataIndex: 'disconnected_at',
      key: 'disconnected_at',
      render: (text) => (text ? new Date(text).toLocaleString('zh-CN') : '-'),
    },
    {
      title: t('deviceManagement.duration'),
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => {
        if (!duration) return '-';
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        const seconds = duration % 60;
        return `${hours}h ${minutes}m ${seconds}s`;
      },
    },
    {
      title: t('deviceManagement.clientIP'),
      dataIndex: 'client_ip',
      key: 'client_ip',
    },
    {
      title: t('deviceManagement.disconnectReason'),
      dataIndex: 'disconnect_reason',
      key: 'disconnect_reason',
      render: (text) => text || '-',
    },
  ];

  return (
    <>
      <Card
        title={t('deviceManagement.title')}
        extra={
          <Space wrap>
            {/* 标签搜索 */}
            <TagSearchInput
              searchTags={searchTags}
              setSearchTags={setSearchTags}
              userTags={userTags}
            />
            <Text type="secondary">{t('common.devices', { count: devices.length })}</Text>
            <ActionButton action="add" onClick={openAddDeviceModal}>
              {t('deviceManagement.add')}
            </ActionButton>
            <ActionButton action="refresh" onClick={loadDevices}>
              {t('common.refresh')}
            </ActionButton>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={devices}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('common.totalRecords', { count: total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          style={{ overflow: 'visible' }}
        />
        <Alert
          type="info"
          showIcon
          message={t('deviceManagement.aliasRule')}
          style={{ marginTop: 16 }}
        />
      </Card>

      {/* 设备详情对话框 */}
      <Modal
        title={
          <Space>
            <MobileOutlined />
            <span>{t('deviceManagement.detailTitle')}</span>
            {selectedDevice && (
              <span className="device-name-with-status">
                <span
                  className={`device-status-dot ${selectedDevice.is_online ? 'online' : 'offline'}`}
                />
                <span
                  className={selectedDevice.is_online ? 'text-green-400' : 'text-dark-600'}
                  style={{ fontSize: 12 }}
                >
                  {selectedDevice.is_online
                    ? t('deviceManagement.online')
                    : t('deviceManagement.offline')}
                </span>
              </span>
            )}
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedDevice(null);
        }}
        footer={
          <ActionButton
            action="close"
            onClick={() => {
              setDetailModalVisible(false);
              setSelectedDevice(null);
            }}
          >
            {t('common.close')}
          </ActionButton>
        }
        width={800}
      >
        {selectedDevice && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* 基本信息 */}
            <Card
              size="small"
              title={
                <>
                  <ProfileOutlined /> {t('deviceManagement.basicInfo')}
                </>
              }
            >
              <Descriptions column={2} size="small">
                <Descriptions.Item label={t('deviceManagement.deviceAlias')}>
                  <Text strong>{selectedDevice.device_alias || '-'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('deviceManagement.runMode')}>
                  {selectedDevice.run_mode === 'user' ? (
                    <Tag color="blue">{t('deviceManagement.runModeUser')}</Tag>
                  ) : (
                    <Tag>{t('deviceManagement.runModeSystem')}</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('deviceManagement.createTime')}>
                  {new Date(selectedDevice.created_at).toLocaleString('zh-CN')}
                </Descriptions.Item>
                <Descriptions.Item label={t('deviceManagement.serialNumber')}>
                  <Text code copyable>
                    {selectedDevice.serial_number}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('deviceManagement.hostname')}>
                  {selectedDevice.hostname || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('deviceManagement.tags')}>
                  {selectedDevice.tags && selectedDevice.tags.length > 0 ? (
                    <Space size={4} wrap>
                      {selectedDevice.tags.map((tag) => (
                        <Tag key={tag} color={getTagColor(tag)}>{tag}</Tag>
                      ))}
                    </Space>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('deviceManagement.lastOnline')}>
                  {selectedDevice.last_seen_at ? (
                    <Tooltip title={new Date(selectedDevice.last_seen_at).toLocaleString('zh-CN')}>
                      <Text>{new Date(selectedDevice.last_seen_at).toLocaleString('zh-CN')}</Text>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('deviceManagement.updateTime')}>
                  {new Date(selectedDevice.updated_at).toLocaleString('zh-CN')}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* 系统信息 */}
            <Card
              size="small"
              title={
                <>
                  <LaptopOutlined /> {t('deviceManagement.systemInfo')}
                </>
              }
            >
              <Descriptions column={2} size="small">
                <Descriptions.Item label={t('deviceManagement.os')}>
                  {selectedDevice.os_type ? (
                    <Tag color="purple">{selectedDevice.os_type}</Tag>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('deviceManagement.osVersion')}>
                  {selectedDevice.os_version || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Space>
        )}
      </Modal>

      {/* 连接历史对话框 */}
      <Modal
        title={
          t('deviceManagement.connectionHistory') +
          ' - ' +
          (selectedDevice?.device_alias || selectedDevice?.serial_number)
        }
        open={historyModalVisible}
        onCancel={() => {
          setHistoryModalVisible(false);
          setConnectionHistory([]);
        }}
        footer={
          <ActionButton
            action="close"
            onClick={() => {
              setHistoryModalVisible(false);
              setConnectionHistory([]);
            }}
          >
            {t('common.close')}
          </ActionButton>
        }
        width={900}
      >
        <Table
          columns={historyColumns}
          dataSource={connectionHistory}
          rowKey="id"
          scroll={{ x: 700 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('common.totalRecords', { count: total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          size="small"
        />
      </Modal>

      {/* 更新设备对话框 */}
      <Modal
        title={t('deviceManagement.editTitle')}
        open={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          updateForm.resetFields();
        }}
        footer={null}
      >
        <Form form={updateForm} onFinish={handleUpdateSubmit} layout="vertical">
          <Form.Item label={t('deviceManagement.deviceAlias')} name="device_alias">
            <Input placeholder={t('deviceManagement.aliasPlaceholder')} />
          </Form.Item>
          {/* 标签编辑 */}
          <Form.Item label={t('deviceManagement.tags')} name="tags">
            <TagInput userTags={userTags} />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <ActionButton
                action="cancel"
                onClick={() => {
                  setUpdateModalVisible(false);
                  updateForm.resetFields();
                }}
              />
              <ActionButton action="save" variant="primary" htmlType="submit" />
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 选择安装令牌生成安装命令（新增设备） */}
      <Modal
        title={t('deviceManagement.selectInstallTokenTitle')}
        open={addDeviceModalVisible}
        onCancel={closeAddDeviceModal}
        footer={[
          <ActionButton key="close" action="close" onClick={closeAddDeviceModal}>
            {t('common.close')}
          </ActionButton>,
        ]}
      >
        {loadingTokens ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : installTokens.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message={t('deviceManagement.installTokenEmpty')}
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>{t('deviceManagement.selectInstallToken')}</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder={t('deviceManagement.selectInstallToken')}
                value={selectedToken ? selectedToken.id : undefined}
                onChange={(id) => {
                  const tok = installTokens.find((x) => x.id === id);
                  setSelectedToken(tok);
                }}
                options={installTokens.map((tok) => ({
                  value: tok.id,
                  label: tok.note ? `${tok.note} (${tok.token.slice(0, 8)}••••)` : tok.token.slice(0, 8) + '••••',
                }))}
              />
            </div>
            <div>
              <Text strong>{t('deviceManagement.username')}</Text>
              <div style={{ marginTop: 4 }}>
                <Text>{currentUsername || '<' + t('deviceManagement.username') + '>'}</Text>
              </div>
            </div>
            {selectedToken && (() => {
              const dlURL = window.PidocBrand?.dlURL || 'https://dl.pidoc.cn';
              const cmd = `curl -fsSL ${dlURL}/pidoc/install.sh | bash -s -- --customtoken '${selectedToken.token}' --username '${currentUsername}'`;
              return (
                <div className="rounded-lg border border-dark-700 bg-dark-900/40 p-3">
                  <Text strong>{t('deviceManagement.installCommandForToken')}</Text>
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <code className="flex-1 px-3 py-2 bg-dark-800 rounded text-xs text-green-400 font-mono whitespace-pre-wrap break-all">
                      {cmd}
                    </code>
                    <ActionButton
                      action="copy"
                      onClick={() => {
                        if (typeof window.copy === 'function') {
                          window.copy(cmd);
                        } else if (navigator.clipboard) {
                          navigator.clipboard.writeText(cmd);
                        }
                        message.success(t('deviceManagement.installCommandCopied'));
                      }}
                    >
                      {t('deviceManagement.copyInstallCommand')}
                    </ActionButton>
                  </div>
                </div>
              );
            })()}
          </Space>
        )}
      </Modal>

    </>
  );
}

// 导出到全局
export { DeviceManagement };
